import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RouteProps } from 'react-router';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { isPlainObject } from 'lodash';
import { RootState } from 'modules';
import { BaseTemplate } from 'components/base/BaseTemplate';
import Core from 'components/base/Core';
import ErrorBoundary from 'components/base/error/ErrorBoundary';
import NoMatch from 'components/base/error/NoMatch';
import Landing from 'components/base/Landing';
import Reviews from 'components/base/Reviews';
import CaseBoard from 'components/case/CaseBoard';
import CaseDetail from 'components/case/CaseDetail';
import CaseItem from 'components/case/CaseItem';
import CaseWrite from 'components/case/CaseWrite';
import CaseIntoroduction from 'components/case/Introduction';
import CaseLibrary from 'components/case/Library';
import CaseLibraryDetail from 'components/case/LibraryDetail';
import CaseLibraryWrite from 'components/case/LibraryWrite';
import FinalAcceptanceDetail from 'components/community/acceptance/FinalAcceptanceDetail';
import FinalAcceptanceList from 'components/community/acceptance/FinalAcceptanceList';
import InterviewReviewList from 'components/community/interview/InterviewReviewList';
import InterviewReviewWrite from 'components/community/interview/InterviewReviewWrite';
import CompanyFulltime from 'components/company/CompanyFulltime';
import CompanyFulltimeView from 'components/company/CompanyFulltimeView';
import CompanyInternFinder from 'components/company/CompanyInternFinder';
import CompanyJobOpening from 'components/company/CompanyJobOpening';
import CompanyParttime from 'components/company/CompanyParttime';
import CompanyParttimeView from 'components/company/CompanyParttimeView';
import CompanySearch from 'components/company/CompanySearch';
import CompanyTurnOver from 'components/company/CompanyTurnOver';
import CSLabBoard, { CSLabBoardDetail } from 'components/cslab/CSLabBoard';
import CSLabMentorFinder from 'components/cslab/CSLabMentorFinder';
import CSLabSwitchToMentor from 'components/cslab/CSLabSwitchToMentor';
import CSWebinar, { CSWebinarDetail } from 'components/cslab/CSWebinar';
import IntroductionAnnouncement, {
  IntroductionAnnouncementDetail,
} from 'components/introduction/IntroductionAnnouncement';
import IntroductionCustomerInquiry from 'components/introduction/IntroductionCustomerInquiry';
import { IntroductionQuestionDetail } from 'components/introduction/IntroductionQuestion';
import { KaitusBaseTemplate } from 'components/kaitus/BaseTemplate';
import KaitusComplete from 'components/kaitus/Complete';
import KaitusLogin from 'components/kaitus/Login';
import KaitusWelcome from 'components/kaitus/Welcome';
import ExampleDetail from 'components/learning/board/ExampleDetail';
import LearningBoard from 'components/learning/board/LearningBoard';
import SubThemeBoard from 'components/learning/board/SubThemeBoard';
import SubThemeDetail from 'components/learning/board/SubThemeDetail';
import MembershipIntroduction from 'components/learning/guide/MembershipIntroduction';
import QuizDetail from 'components/learning/quiz/QuizDetail';
import QuizList from 'components/learning/quiz/QuizList';
import MentoringCreditPurchaseCompleted from 'components/mentoring/CreditPurchaseCompleted';
import MentoringDashboard from 'components/mentoring/Dashboard';
import MentoringMyMentee from 'components/mentoring/mentor/MentoringMyMentee';
import MentoringAbout from 'components/mentoring/MentoringAbout';
import MentoringClassCreate from 'components/mentoring/MentoringClassCreate';
import MentoringClassView from 'components/mentoring/MentoringClassView';
import MentoringCreditPrice from 'components/mentoring/MentoringCreditPrice';
import MentoringFeedback from 'components/mentoring/MentoringFeedback';
import MentoringMenteeView from 'components/mentoring/MentoringMenteeView';
import MentoringMentorList from 'components/mentoring/MentoringMentorList';
import MentoringMentorView from 'components/mentoring/MentoringMentorView';
import MentoringMyPage from 'components/mentoring/MentoringMyPage';
import MentoringReview from 'components/mentoring/MentoringReview';
import MentoringQNA from 'components/mentoring/qna/MentoringQNA';
import MentoringQNADetail from 'components/mentoring/qna/MentoringQNADetail';
import MentoringQNAWrite from 'components/mentoring/qna/MentoringQNAWrite';
import ProgramAbout from 'components/programAbout/ProgramAbout';
import Purchase from 'components/purchase/';
import PurchaseComplete from 'components/purchase/Complete';
import Activate from 'components/user/Activate';
import Edit from 'components/user/Edit';
import Login from 'components/user/Login';
import MentorVerification from 'components/user/MentorVerification';
import PasswordReset from 'components/user/PasswordReset';
import PasswordUpdate from 'components/user/PasswordUpdate';
import SignupCompleted from 'components/user/signup/SignupCompleted';
import VerificationEmail from 'components/user/signup/VerificationEmail';
import SignupStep from 'components/user/SignupStep';
import UserCourseList from 'components/user/UserCourseList';
import UserCourseUsageHistory from 'components/user/UserCourseUsageHistory';
import UserCreditExchange from 'components/user/UserCreditExchange';
import UserCreditExchangeRequest from 'components/user/UserCreditExchangeRequest';
// import MentoringVideoCall from 'components/videoCall/MentoringVideoCall';
import { main } from 'libs/api/user';
import { CoreProvider } from 'libs/contexts/CoreContext';
import { TUser, setUserInfo } from 'modules/user';
import { ERecruitmentCategory, EUserType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import FinalAcceptanceWrite from './community/acceptance/FinalAcceptanceWrite';
import ExcelClassList from './excelClass/ExcelClassList';
import ExcelClassPlayer from './excelClass/ExcelClassPlayer';
import InterviewMasterClassBasicPage from './interviewMasterClassBasic/InterviewMasterClassBasicPage';
import InterviewMasterClassBasicPlayerPage from './interviewMasterClassBasic/InterviewMasterClassBasicPlayerPage';
import KaitusAssessment from './kaitus/Assessment';
import KaitusAssessmentDetail from './kaitus/AssessmentDetail';
import PrivacyPolicy from './privacyPolicy/PrivacyPolicy';
import DirectCallMain from './videoCall/components/pages/DirectCallMain';

const data = main();
const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  if (currentUser === undefined) {
    const response: TUser = data.read();

    if (isPlainObject(response)) {
      dispatch(setUserInfo(response));
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  function redirectAfterLogin(component: React.ComponentElement<any, any>) {
    return currentUser ? <Redirect to='/' /> : component;
  }

  return (
    <IntlProvider locale={'ko-KR'}>
      <CoreProvider>
        {/* Core.tsx 맨아래에서 맨 위로 올림 (페이지 이동할 때 아래 컴포넌트의 각각 helmet에 따라 title 바뀌기 때문) */}
        <Core />
        <ErrorBoundary>
          {/* <SbCallsProvider appId=''> */}
          <Switch>
            <Route exact path={['/', '/en']}>
              <BaseTemplate>
                {currentUser ? (
                  currentUser.user_type === EUserType.Kaitus ? (
                    <Redirect to={`${ERoute.KaitusWelcome.replace(':tenant_id', currentUser.nickname)}`} />
                  ) : currentUser.user_type === EUserType.Mentee ? (
                    <Redirect to={ERoute.MentoringMyPage} />
                  ) : currentUser.is_verified === true ? (
                    <Redirect to={ERoute.MentoringMyPage} />
                  ) : (
                    <Redirect to={ERoute.IntroductionLanding} />
                  )
                ) : (
                  <Landing />
                )}
              </BaseTemplate>
            </Route>
            <Route path={ERoute.UserLogin}>
              {redirectAfterLogin(
                <BaseTemplate>
                  <Login />
                </BaseTemplate>,
              )}
            </Route>

            {/* Kaitus Addition */}
            <Route path={ERoute.KaitusLogin}>
              {redirectAfterLogin(
                <KaitusBaseTemplate>
                  <KaitusLogin />
                </KaitusBaseTemplate>,
              )}
            </Route>
            <Route path={ERoute.KaitusWelcome}>
              <KaitusBaseTemplate>
                <KaitusWelcome />
              </KaitusBaseTemplate>
            </Route>
            <Route path={ERoute.KaitusComplete}>
              <KaitusBaseTemplate>
                <KaitusComplete />
              </KaitusBaseTemplate>
            </Route>
            <Route exact path={ERoute.KaitusAssessment}>
              <KaitusBaseTemplate>
                <KaitusAssessment />
              </KaitusBaseTemplate>
            </Route>
            <Route exact path={ERoute.KaitusAssessmentDetail}>
              <KaitusBaseTemplate>
                <KaitusAssessmentDetail />
              </KaitusBaseTemplate>
            </Route>

            <Route exact path={ERoute.UserSignup}>
              {redirectAfterLogin(<SignupStep />)}
            </Route>
            <Route exact path={ERoute.UserPasswordReset}>
              {redirectAfterLogin(
                <BaseTemplate>
                  <PasswordReset />
                </BaseTemplate>,
              )}
            </Route>
            <Route path={ERoute.UserSignupCompleted}>
              {
                <BaseTemplate>
                  <SignupCompleted />
                </BaseTemplate>
              }
            </Route>
            <Route path={`${ERoute.UserActivate}/:uidb64/:token`}>
              <BaseTemplate>
                <Activate />
              </BaseTemplate>
            </Route>
            <Route path={`${ERoute.PasswordUpdate}/:uidb64/:token`}>
              <BaseTemplate>
                <PasswordUpdate />
              </BaseTemplate>
            </Route>
            <Route path={`${ERoute.VerificationEmail}/:uidb64`}>
              <BaseTemplate>
                <VerificationEmail />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.IntroductionLanding}>
              <BaseTemplate>
                <Landing />
              </BaseTemplate>
            </Route>
            <Route exact path={`${ERoute.IntroductionReview}/:operation`}>
              <BaseTemplate>
                <Reviews />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.IntroductionCustomerInquiry}>
              <BaseTemplate>
                <IntroductionCustomerInquiry />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.IntroductionQuestion}>
              <BaseTemplate>
                <IntroductionCustomerInquiry />
              </BaseTemplate>
            </Route>
            <PrivateRoute path={`${ERoute.IntroductionQuestion}/:operation`}>
              <BaseTemplate>
                <IntroductionQuestionDetail />
              </BaseTemplate>
            </PrivateRoute>
            <Route exact path={ERoute.IntroductionAnnouncement}>
              <BaseTemplate>
                <IntroductionAnnouncement />
              </BaseTemplate>
            </Route>
            <Route path={`${ERoute.IntroductionAnnouncement}/:operation`}>
              <BaseTemplate>
                <IntroductionAnnouncementDetail />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.MentoringProgramAbout}>
              <BaseTemplate>
                <ProgramAbout />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.UserEdit}>
              <BaseTemplate>
                <Edit />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.UserCredit}>
              <BaseTemplate>
                <UserCourseList />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.UserCourseUsageHistory}>
              <BaseTemplate>
                <UserCourseUsageHistory />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.UserCreditExchange}>
              <BaseTemplate>
                <UserCreditExchange />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.UserCreditExchangeRequest}>
              <BaseTemplate>
                <UserCreditExchangeRequest />
              </BaseTemplate>
            </PrivateRoute>

            <PrivateRoute exact path={ERoute.MentorVerification}>
              <BaseTemplate>
                <MentorVerification />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringMyPage}>
              <BaseTemplate>
                <MentoringMyPage />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringMyMentee}>
              <BaseTemplate>
                <MentoringMyMentee />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringAbout}>
              <BaseTemplate>
                <MentoringAbout />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringCreditPrice}>
              <BaseTemplate>
                <MentoringCreditPrice />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringCreditPurchaseCompleted}>
              <BaseTemplate>
                <MentoringCreditPurchaseCompleted />
              </BaseTemplate>
            </PrivateRoute>
            <Route exact path={ERoute.MentoringMentorList}>
              <BaseTemplate>
                <MentoringMentorList />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.MentoringMentorView}>
              <BaseTemplate>
                <MentoringMentorView />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringMenteeView}>
              <BaseTemplate>
                <MentoringMenteeView />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.CompanyInternView}>
              <BaseTemplate>
                <MentoringMenteeView isInternView={true} />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringClassCreate}>
              <BaseTemplate>
                <MentoringClassCreate />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.MentoringClassView}>
              <BaseTemplate>
                <MentoringClassView />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute path={`${ERoute.MentoringFeedback}/:operation`}>
              <BaseTemplate>
                <MentoringFeedback />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute path={`${ERoute.MentoringReview}/:operation`}>
              <BaseTemplate>
                <MentoringReview />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute path={`${ERoute.MentoringDashboard}`}>
              <BaseTemplate>
                <MentoringDashboard />
              </BaseTemplate>
            </PrivateRoute>

            <PrivateRoute exact path={ERoute.CompanyTurnOver}>
              <BaseTemplate>
                <CompanyTurnOver />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.CompanyTurnOverView}>
              <BaseTemplate>
                <CompanyFulltimeView category={ERecruitmentCategory.TurnOver} />
              </BaseTemplate>
            </PrivateRoute>
            <Route exact path={ERoute.CompanyFulltime}>
              <BaseTemplate>
                <CompanyFulltime />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CompanyFulltimeView}>
              <BaseTemplate>
                <CompanyFulltimeView category={ERecruitmentCategory.Fulltime} />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CompanyParttime}>
              <BaseTemplate>
                <CompanyParttime />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CompanyParttimeView}>
              <BaseTemplate>
                <CompanyParttimeView />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.CompanyInternFinder}>
              <BaseTemplate>
                <CompanyInternFinder />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.CompanyJobOpening}>
              <BaseTemplate>
                <CompanyJobOpening />
              </BaseTemplate>
            </PrivateRoute>
            <Route exact path={ERoute.CompanySearch}>
              <BaseTemplate>
                <CompanySearch />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CSLabBoard}>
              <BaseTemplate>
                <CSLabBoard />
              </BaseTemplate>
            </Route>
            <Route path={`${ERoute.CSLabBoard}/:operation`}>
              <BaseTemplate>
                <CSLabBoardDetail />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CSWebinar}>
              <BaseTemplate>
                <CSWebinar />
              </BaseTemplate>
            </Route>
            <Route path={`${ERoute.CSWebinar}/:operation`}>
              <BaseTemplate>
                <CSWebinarDetail />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.CSLabMentorFinder}>
              <BaseTemplate>
                <CSLabMentorFinder />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute exact path={ERoute.CSLabSwitchToMentor}>
              <BaseTemplate>
                <CSLabSwitchToMentor />
              </BaseTemplate>
            </PrivateRoute>

            <PrivateRoute exact path={ERoute.Purchase}>
              <BaseTemplate>
                <Purchase />
              </BaseTemplate>
            </PrivateRoute>
            <PrivateRoute path={ERoute.PurchaseComplete}>
              <BaseTemplate>
                <PurchaseComplete />
              </BaseTemplate>
            </PrivateRoute>

            <Route path={ERoute.CaseIntroduction}>
              <BaseTemplate>
                <CaseIntoroduction />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CaseBoard}>
              <BaseTemplate>
                <CaseBoard />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.CaseItem}>
              <BaseTemplate>
                <CaseItem />
              </BaseTemplate>
            </PrivateRoute>
            <Route exact path={ERoute.CaseWrite}>
              <BaseTemplate>
                <CaseWrite />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CaseWriteEdit}>
              <BaseTemplate>
                <CaseWrite />
              </BaseTemplate>
            </Route>
            <Route path={ERoute.CaseDetail}>
              <BaseTemplate>
                <CaseDetail />
              </BaseTemplate>
            </Route>
            {/* 케이스 qna */}
            <Route exact path={ERoute.MentoringQNA}>
              <BaseTemplate>
                <MentoringQNA />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.MentoringQNAWrite}>
              <BaseTemplate>
                <MentoringQNAWrite />
              </BaseTemplate>
            </PrivateRoute>
            <Route path={ERoute.MentoringQNADetail}>
              <BaseTemplate>
                <MentoringQNADetail />
              </BaseTemplate>
            </Route>
            {/* 케이스 library */}
            <Route exact path={ERoute.CaseLibrary}>
              <BaseTemplate>
                <CaseLibrary />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CaseLibraryWrite}>
              <BaseTemplate>
                <CaseLibraryWrite />
              </BaseTemplate>
            </Route>
            <Route path={ERoute.CaseLibraryDetail}>
              <BaseTemplate>
                <CaseLibraryDetail />
              </BaseTemplate>
            </Route>
            {/* 🌸 케이스 스터디 부분 로그인 전에도 보여아하므로 PrivateRoute없애주기*/}
            {/* CSLearning 게시판 */}
            <Route exact path={ERoute.CSLearningBoard}>
              <BaseTemplate>
                <LearningBoard />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CSLearningSubcategory}>
              <BaseTemplate>
                <SubThemeBoard />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CSLearningExplain}>
              <BaseTemplate>
                <SubThemeDetail />
              </BaseTemplate>
            </Route>
            <Route exact path={ERoute.CSLearningExample}>
              <BaseTemplate>
                <ExampleDetail />
              </BaseTemplate>
            </Route>
            {/* CSLearning 가이드 페이지 */}
            <Route exact path={ERoute.CSLearningPurchaseGuide}>
              <BaseTemplate>
                <MembershipIntroduction />
              </BaseTemplate>
            </Route>
            {/* CSLearning 문제풀기 */}
            <Route exact path={ERoute.CSLearningQuizList}>
              <BaseTemplate>
                <QuizList />
              </BaseTemplate>
            </Route>
            <Route excat path={ERoute.PrivacyPolicy}>
              <PrivacyPolicy />
            </Route>

            {/* view모드 혹은 수정모드 */}
            <PrivateRoute exact path={`${ERoute.CSLearningQuiz}/:operation/:id`}>
              <BaseTemplate>
                <QuizDetail />
              </BaseTemplate>
            </PrivateRoute>
            {/* 초기 작성 모드 */}
            <PrivateRoute exact path={`${ERoute.CSLearningQuiz}/:operation`}>
              <BaseTemplate>
                <QuizDetail />
              </BaseTemplate>
            </PrivateRoute>

            {/* 커뮤니티 면접 후기 */}
            <Route exact path={ERoute.InterviewReviewBoard}>
              <BaseTemplate>
                <InterviewReviewList />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.InterviewReviewWrite}>
              <BaseTemplate>
                <InterviewReviewWrite />
              </BaseTemplate>
            </PrivateRoute>

            {/* 커뮤니티 최종 합격 후기 */}
            <Route exact path={ERoute.FinalAcceptanceBoard}>
              <BaseTemplate>
                <FinalAcceptanceList />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={`${ERoute.FinalAcceptanceWrite}`}>
              <BaseTemplate>
                <FinalAcceptanceWrite />
              </BaseTemplate>
            </PrivateRoute>
            <Route exact path={ERoute.FinalAcceptanceDetail}>
              <BaseTemplate>
                <FinalAcceptanceDetail />
              </BaseTemplate>
            </Route>

            <Route exact path={ERoute.ExcelClassList}>
              <BaseTemplate>
                <ExcelClassList />
              </BaseTemplate>
            </Route>
            <PrivateRoute exact path={ERoute.ExcelClassPlayer}>
              <BaseTemplate>
                <ExcelClassPlayer />
              </BaseTemplate>
            </PrivateRoute>

            <Route exact path={ERoute.LectureInterviewMasterClassBasic}>
              <BaseTemplate>
                <InterviewMasterClassBasicPage />
              </BaseTemplate>
            </Route>

            <PrivateRoute exact path={ERoute.LectureInterviewMasterClassBasicVideoPlayer}>
              <BaseTemplate>
                <InterviewMasterClassBasicPlayerPage />
              </BaseTemplate>
            </PrivateRoute>

            {/* <PrivateRoute exact path={ERoute.MentoringJoin}>
              <BaseTemplate>
                <MentoringVideoCall />
              </BaseTemplate>
            </PrivateRoute> */}
            {/* <PrivateRoute exact path={ERoute.CaseroomJoin}>
              <BaseTemplate>
                <ExcelClassPlayer />
              </BaseTemplate>
            </PrivateRoute> */}
            {/* <PrivateRoute exact path={'/course'}>
              <BaseTemplate>
                <LandingPage />
              </BaseTemplate>
            </PrivateRoute> */}

            <Route exact path={'/direct-call'}>
              <DirectCallMain />
            </Route>

            <Route path='*' component={NoMatch} />
          </Switch>
          {/* </SbCallsProvider> */}
        </ErrorBoundary>
      </CoreProvider>
    </IntlProvider>
  );
};

type TPrivateRoute = RouteProps & {
  children?: React.ReactNode;
  checkAuth?: boolean;
};

const PrivateRoute = ({ children, checkAuth, ...rest }: TPrivateRoute) => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  return (
    <Route
      {...rest}
      render={({ location }) =>
        currentUser ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: `${ERoute.UserLogin}`,
              search: `?next=${location.pathname}${location.search}`,
            }}
          />
        )
      }
    />
  );
};

export default App;
